import {
  SessionBuilder,
  SessionCipher,
  SignalProtocolAddress,
} from "@privacyresearch/libsignal-protocol-typescript";
import type {
  StorageType,
  MessageType,
} from "@privacyresearch/libsignal-protocol-typescript";
import type { DeviceType } from "@privacyresearch/libsignal-protocol-typescript/lib/session-types";
import { fetchBundle } from "./kds_client";

/**
 * Single source-of-truth for the device identifier assigned to a browser
 * install in the Signal Protocol address space. iOS-mirrored (see
 * docs/web-device-id.md). Treated as a constant for now — multi-tab work
 * will turn this into a per-install value at provisioning time.
 */
export const WEB_DEVICE_ID = 1;

function b642ab(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Curve25519 / Ed25519 DJB type byte. The Signal Protocol serializes
 * 32-byte public keys with a leading 0x05 type marker, producing a
 * 33-byte form. iOS Signal stacks (e.g. `kaether_for_ios`) frequently
 * upload the raw 32-byte form to KDS without the marker, while
 * `@privacyresearch/libsignal-protocol-typescript` on web only accepts
 * the 33-byte serialized form and logs the warning
 *
 *     "Expected pubkey of length 33, please report the ST and client
 *      that generated the pubkey"
 *
 * before failing signature verification with `Error: Invalid signature`.
 *
 * Normalising on the consumer side is safer than rewriting historical
 * bundles on KDS: both formats stay valid on the wire, and the web
 * client just lifts 32-byte pubkeys into the 33-byte form it expects.
 */
const DJB_TYPE = 0x05;

function normalizePubKey(buf: ArrayBuffer): ArrayBuffer {
  const view = new Uint8Array(buf);
  if (view.byteLength === 33) return buf;
  if (view.byteLength === 32) {
    const out = new Uint8Array(33);
    out[0] = DJB_TYPE;
    out.set(view, 1);
    return out.buffer;
  }
  // Any other length is a real bundle bug — let libsignal surface it.
  return buf;
}

function decodePubKey(base64: string): ArrayBuffer {
  return normalizePubKey(b642ab(base64));
}

export async function establishSession(
  store: StorageType,
  remoteUserId: string,
  remoteDeviceId: number,
): Promise<boolean> {
  const address = new SignalProtocolAddress(
    remoteUserId,
    remoteDeviceId,
  );

  const existingSession = await store.loadSession(address.toString());
  if (existingSession) return true;

  const bundle = await fetchBundle(
    remoteUserId,
    String(remoteDeviceId),
  );
  if (!bundle) return false;

  const device: DeviceType = {
    identityKey: decodePubKey(bundle.identityPubKey),
    signedPreKey: {
      keyId: bundle.signedPreKey.keyId,
      publicKey: decodePubKey(bundle.signedPreKey.publicKey),
      // Signature is variable-length (64 bytes for Ed25519), DO NOT
      // normalize — only pubkeys carry the 0x05 type byte.
      signature: b642ab(bundle.signedPreKey.signature),
    },
    registrationId: bundle.registrationId,
  };

  if (bundle.oneTimePreKey) {
    device.preKey = {
      keyId: bundle.oneTimePreKey.keyId,
      publicKey: decodePubKey(bundle.oneTimePreKey.publicKey),
    };
  }

  const builder = new SessionBuilder(store, address);
  try {
    await builder.processPreKey(device);
  } catch (err) {
    // Cross-platform signature mismatches and partial-write KDS
    // states both surface here as a bare "Invalid signature". The
    // raw error has no hint about WHICH key, WHICH bundle, or
    // WHETHER the local identity matches what KDS returned — log
    // enough context that the next [send] failure produces a
    // self-diagnosing trace.
    const localId = await (
      store as unknown as {
        getIdentityKeyPair: () => Promise<
          { pubKey: ArrayBuffer; privKey: ArrayBuffer } | undefined
        >;
      }
    )
      .getIdentityKeyPair()
      .catch(() => undefined);
    const ab2b64 = (buf: ArrayBuffer) =>
      btoa(String.fromCharCode(...new Uint8Array(buf)));
    console.error(
      "[e2ee] establishSession failed during processPreKey:",
      err,
      {
        remoteUserId,
        remoteDeviceId,
        remoteIdentityPubKey_b64: bundle.identityPubKey,
        remoteSignedPreKey_publicKey_b64: bundle.signedPreKey.publicKey,
        remoteSignedPreKey_signature_b64: bundle.signedPreKey.signature,
        remoteIdentityPubKey_byteLen: new Uint8Array(
          b642ab(bundle.identityPubKey),
        ).byteLength,
        remoteSignedPreKey_publicKey_byteLen: new Uint8Array(
          b642ab(bundle.signedPreKey.publicKey),
        ).byteLength,
        remoteSignedPreKey_signature_byteLen: new Uint8Array(
          b642ab(bundle.signedPreKey.signature),
        ).byteLength,
        localIdentityPubKey_b64: localId ? ab2b64(localId.pubKey) : null,
        localIdentityPubKey_byteLen: localId
          ? new Uint8Array(localId.pubKey).byteLength
          : null,
        identityKeyMatchesLocal:
          !!localId && bundle.identityPubKey === ab2b64(localId.pubKey),
        diagnosticHint:
          "If identityKeyMatchesLocal is true and the error is " +
          "'Invalid signature', the signedPreKey on KDS was not " +
          "signed by the current identity — typically a partial " +
          "KDS PUT that updated identityPubKey but kept a stale " +
          "signedPreKey. Re-run Settings → Encryption → Reset " +
          "E2EE Keys; the new flow probes processPreKey and " +
          "reports the exact field that disagrees.",
      },
    );
    throw err;
  }

  return true;
}

export async function encryptMessage(
  store: StorageType,
  remoteUserId: string,
  remoteDeviceId: number,
  plaintext: Uint8Array,
): Promise<MessageType> {
  const address = new SignalProtocolAddress(
    remoteUserId,
    remoteDeviceId,
  );
  const cipher = new SessionCipher(store, address);
  return cipher.encrypt(plaintext.buffer as ArrayBuffer);
}

export async function decryptMessage(
  store: StorageType,
  senderUserId: string,
  senderDeviceId: number,
  ciphertext: Uint8Array,
  isPreKeyMessage: boolean,
): Promise<Uint8Array> {
  const address = new SignalProtocolAddress(
    senderUserId,
    senderDeviceId,
  );
  const cipher = new SessionCipher(store, address);

  let plainBuffer: ArrayBuffer;
  if (isPreKeyMessage) {
    plainBuffer = await cipher.decryptPreKeyWhisperMessage(
      ciphertext.buffer as ArrayBuffer,
      "binary",
    );
  } else {
    plainBuffer = await cipher.decryptWhisperMessage(
      ciphertext.buffer as ArrayBuffer,
      "binary",
    );
  }

  return new Uint8Array(plainBuffer);
}

export function getSessionCipher(
  store: StorageType,
  remoteUserId: string,
  remoteDeviceId: number,
): SessionCipher {
  const address = new SignalProtocolAddress(
    remoteUserId,
    remoteDeviceId,
  );
  return new SessionCipher(store, address);
}
